import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("vip_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Sliding admin session — every successful authenticated admin request gets
// a freshly-issued JWT in `X-Auth-Refresh`. Swap our stored token so the
// next call ships the fresh expiry. Customer tokens are not refreshed
// server-side (they have a 7-day TTL), so this is a no-op for them.
api.interceptors.response.use(
  (response) => {
    const refreshed = response.headers?.["x-auth-refresh"];
    if (refreshed) {
      localStorage.setItem("vip_token", refreshed);
    }
    return response;
  },
  (error) => {
    // Idle-timeout 401 surface — backend returns this exact detail string.
    // The window event lets AuthContext / route guards react without each
    // page having to wire its own listener.
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 401 && typeof detail === "string" && /session expired due to inactivity/i.test(detail)) {
      try {
        localStorage.removeItem("vip_token");
        window.dispatchEvent(new CustomEvent("npc:session-expired", { detail }));
      } catch (_e) { /* ignore — best-effort cleanup */ }
    }
    return Promise.reject(error);
  },
);

// Safely coerce a FastAPI error response into a human-readable string.
// FastAPI returns `detail` as either a string ("Forbidden") OR an array of
// Pydantic ValidationError objects ([{loc, msg, type}, …]). Passing the
// array directly to React/sonner crashes the render tree with "Objects are
// not valid as a React child", producing a white screen.
export const apiErr = (err, fallback = "Request failed") => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((e) => {
        const field = Array.isArray(e?.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
        const msg = e?.msg || "invalid";
        return field ? `${field}: ${msg}` : msg;
      })
      .join("; ");
  }
  if (d && typeof d === "object") return d.message || JSON.stringify(d);
  return err?.message || fallback;
};

// Streams a binary endpoint to a file download in the browser.
export const downloadFile = async (path, filename) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default api;
